import { useState, useEffect, useCallback, useRef } from 'react';
import { getStoredUserId } from '../utils/analytics';
import { WS_BASE_URL } from '../utils/apiConfig';

/**
 * Custom hook for WebSocket connection to Polymarket trades feed
 * Pattern from useWebSocket.js but simplified for Polymarket
 *
 * @param {Object} filters - Subscription filters
 * @param {number} filters.minVolume - Minimum volume threshold ($)
 * @param {Object} options - Additional options
 * @param {boolean} options.enabled - Whether to enable WebSocket connection (default: true)
 */
export function usePolymarketWebSocket(filters, options = {}) {
  const { enabled = true } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [trades, setTrades] = useState([]);
  const [topMarkets, setTopMarkets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [historicalLoading, setHistoricalLoading] = useState(true);

  const wsRef = useRef(null);
  const reconnectDelayRef = useRef(1000);
  const maxReconnectDelay = 30000;
  const isConnectingRef = useRef(false);
  const shouldReconnectRef = useRef(true);
  const reconnectTimeoutRef = useRef(null);
  const filtersRef = useRef(filters);
  const isSubscribedRef = useRef(false);

  // Subscribe to trades feed with filters
  const subscribe = useCallback((ws, filters) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn('⚠️ [Polymarket] Cannot subscribe: WebSocket not open');
      return;
    }

    const subscriptionMessage = {
      type: 'subscribe',
      filters: {
        minVolume: filters.minVolume || 10000
      }
    };

    // Add categories if specified
    if (filters.categories && filters.categories.length > 0) {
      subscriptionMessage.filters.categories = filters.categories;
    }

    // Add markets if specified
    if (filters.markets && filters.markets.length > 0) {
      subscriptionMessage.filters.markets = filters.markets;
    }

    console.log('📤 [Polymarket] Subscribing with filters:', subscriptionMessage.filters);
    ws.send(JSON.stringify(subscriptionMessage));
  }, []);

  // Update filters for existing connection
  const updateFilters = useCallback((newFilters) => {
    filtersRef.current = newFilters;

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && isSubscribedRef.current) {
      const updateMessage = {
        type: 'update_filters',
        filters: {
          minVolume: newFilters.minVolume || 10000
        }
      };

      // Add categories if specified
      if (newFilters.categories && newFilters.categories.length > 0) {
        updateMessage.filters.categories = newFilters.categories;
      }

      // Add markets if specified
      if (newFilters.markets && newFilters.markets.length > 0) {
        updateMessage.filters.markets = newFilters.markets;
      }

      console.log('🔄 [Polymarket] Updating filters:', updateMessage.filters);
      wsRef.current.send(JSON.stringify(updateMessage));
    }
  }, []);

  // Handle incoming Polymarket trade
  const handleTrade = useCallback((trade) => {
    const enrichedTrade = {
      ...trade,
      timestamp: trade.timestamp || new Date(),
      isHistorical: false
    };

    setTrades(prev => {
      const updated = [enrichedTrade, ...prev].slice(0, 500);
      return updated;
    });
  }, []);

  // Handle historical data
  const handleHistoricalData = useCallback((data) => {
    console.log(`📚 [Polymarket] Processing ${data.count} historical trades...`);

    const enrichedTrades = data.trades.map(trade => ({
      ...trade,
      timestamp: trade.timestamp ? new Date(trade.timestamp) : new Date(),
      isHistorical: true
    }));

    setTrades(enrichedTrades);
    setHistoricalLoading(false);
    console.log('✅ [Polymarket] Historical trades loaded');
  }, []);

  // Handle top markets update
  const handleTopMarkets = useCallback((data) => {
    setTopMarkets(data);
  }, []);

  // Handle summary update
  const handleSummary = useCallback((data) => {
    setSummary(data);
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (isConnectingRef.current) return;

    isConnectingRef.current = true;

    // Use WS_BASE_URL if set (production), otherwise go through Vite proxy on same host
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const baseUrl = WS_BASE_URL ? `${WS_BASE_URL}/polymarket` : `${wsProtocol}//${window.location.host}/polymarket`;

    // Add userId for unique user tracking (getStoredUserId creates it if not exists)
    const userId = getStoredUserId();
    const wsUrl = `${baseUrl}?userId=${encodeURIComponent(userId)}`;

    console.log('🔌 [Polymarket] Connecting to WebSocket...', wsUrl);
    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = () => {
      console.log('✅ [Polymarket] Connected to trades feed');
      setIsConnected(true);
      reconnectDelayRef.current = 1000;
      isConnectingRef.current = false;

      // Subscribe with filters after connection is established
      subscribe(wsRef.current, filtersRef.current);
    };

    wsRef.current.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);

        switch (message.type) {
          case 'connection':
            console.log('📡 [Polymarket]', message.message);
            break;

          case 'subscription_success':
            console.log('✅ [Polymarket] Subscription successful:', message.filters);
            setIsSubscribed(true);
            isSubscribedRef.current = true;
            setSubscriptionError(null);
            break;

          case 'subscription_error':
            console.error('❌ [Polymarket] Subscription failed:', message.error);
            setIsSubscribed(false);
            isSubscribedRef.current = false;
            setSubscriptionError(message.error);
            break;

          case 'filters_updated':
            console.log('✅ [Polymarket] Filters updated:', message.filters);
            break;

          case 'filter_update_error':
            console.error('❌ [Polymarket] Filter update failed:', message.error);
            break;

          case 'polymarket_trade':
            if (isSubscribedRef.current) {
              handleTrade(message.data);
            }
            break;

          case 'market_trade':
            // Trade for a specific market subscription
            if (isSubscribedRef.current) {
              handleTrade(message.data);
            }
            break;

          case 'historical_data':
            handleHistoricalData(message.data);
            break;

          case 'top_markets':
            handleTopMarkets(message.data);
            break;

          case 'summary':
            handleSummary(message.data);
            break;

          case 'pong':
            // Ping response, ignore
            break;

          case 'error':
            console.error('❌ [Polymarket] Server error:', message.message);
            break;

          default:
            console.warn('⚠️ [Polymarket] Unknown message type:', message.type);
            break;
        }
      } catch (error) {
        console.error('❌ [Polymarket] Error parsing message:', error);
      }
    };

    wsRef.current.onerror = (error) => {
      console.error('❌ [Polymarket] WebSocket error:', error);
      isConnectingRef.current = false;
    };

    wsRef.current.onclose = () => {
      console.log('🔌 [Polymarket] Disconnected from trades feed');
      setIsConnected(false);
      setIsSubscribed(false);
      isSubscribedRef.current = false;
      isConnectingRef.current = false;

      // Only reconnect if we should (not unmounted)
      if (shouldReconnectRef.current) {
        console.log(`⏳ [Polymarket] Reconnecting in ${reconnectDelayRef.current / 1000}s...`);
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, reconnectDelayRef.current);

        reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, maxReconnectDelay);
      }
    };
  }, [subscribe, handleTrade, handleHistoricalData, handleTopMarkets, handleSummary]);

  // Update filters when they change
  useEffect(() => {
    filtersRef.current = filters;
    updateFilters(filters);
  }, [filters, updateFilters]);

  // Connect when enabled, disconnect when disabled
  useEffect(() => {
    if (!enabled) {
      console.log('⏸️ [Polymarket] WebSocket disabled');
      return;
    }

    console.log('▶️ [Polymarket] WebSocket enabled, connecting...');
    shouldReconnectRef.current = true;
    connect();

    return () => {
      // Stop reconnection attempts
      shouldReconnectRef.current = false;

      // Clear any pending reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Close WebSocket
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Request top markets
  const requestTopMarkets = useCallback((limit = 10) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('⚠️ [Polymarket] Cannot request top markets: WebSocket not open');
      return;
    }

    wsRef.current.send(JSON.stringify({
      type: 'request_top_markets',
      limit
    }));
  }, []);

  // Request summary
  const requestSummary = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('⚠️ [Polymarket] Cannot request summary: WebSocket not open');
      return;
    }

    wsRef.current.send(JSON.stringify({
      type: 'request_summary'
    }));
  }, []);

  // Subscribe to specific market
  const subscribeToMarket = useCallback((marketSlug) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('⚠️ [Polymarket] Cannot subscribe to market: WebSocket not open');
      return;
    }

    wsRef.current.send(JSON.stringify({
      type: 'subscribe_market',
      marketSlug
    }));
    console.log('🔔 [Polymarket] Subscribed to market:', marketSlug);
  }, []);

  // Unsubscribe from specific market
  const unsubscribeFromMarket = useCallback((marketSlug) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }

    wsRef.current.send(JSON.stringify({
      type: 'unsubscribe_market',
      marketSlug
    }));
    console.log('🔕 [Polymarket] Unsubscribed from market:', marketSlug);
  }, []);

  // Manual subscribe
  const manualSubscribe = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      subscribe(wsRef.current, filtersRef.current);
    } else {
      console.warn('⚠️ [Polymarket] Cannot subscribe: WebSocket not connected');
    }
  }, [subscribe]);

  // Manual unsubscribe
  const manualUnsubscribe = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log('📤 [Polymarket] Unsubscribing from trades feed');
      wsRef.current.send(JSON.stringify({ type: 'unsubscribe' }));
      setIsSubscribed(false);
      isSubscribedRef.current = false;
      setSubscriptionError(null);
    }
  }, []);

  return {
    isConnected,
    isSubscribed,
    subscriptionError,
    trades,
    topMarkets,
    summary,
    historicalLoading,
    subscribe: manualSubscribe,
    unsubscribe: manualUnsubscribe,
    updateFilters,
    requestTopMarkets,
    requestSummary,
    subscribeToMarket,
    unsubscribeFromMarket
  };
}
